// Word-type detection over §9 morphology entries (the stored word-structure
// shape) — shared by TopicDetail's color pills and the Iʿrāb tool.
// The stem ("root") segment's labels name the word type.

const ARABIC_DIACRITICS_REGEX =
  /[ؐ-ًؚ-ٰٟۖ-ۭ]/g;
const PUNCTUATION_REGEX = /[.,!?؟،؛:"«»()[\]]/g;

function bareLabel(str) {
  if (!str) return "";
  return str
    .replace(ARABIC_DIACRITICS_REGEX, "")
    .replace(PUNCTUATION_REGEX, "")
    .trim();
}

export function morphStemLabel(entry) {
  const segs = entry?.segments || [];
  const stem = segs.find((s) => s.type === "root") || segs[0] || {};
  return `${bareLabel(stem.labelAr || "")} ${stem.labelEn || ""}`;
}

export function morphIsVerb(entry) {
  const label = morphStemLabel(entry);
  return /(^|[\s—(])فعل/.test(label) || /\bverb\b/i.test(label);
}

// Whole word IS a pronoun (ضمير منفصل). Attached suffixes are handled
// separately by morphPronounSuffixSplit.
export function morphIsPronounWord(entry) {
  const label = morphStemLabel(entry);
  return label.includes("ضمير") || /\bpronoun\b/i.test(label);
}

export function morphIsPreposition(entry) {
  const label = morphStemLabel(entry);
  return label.includes("حرف جر") || /\bpreposition\b/i.test(label);
}

export function morphIsNoun(entry) {
  if (morphIsVerb(entry) || morphIsPronounWord(entry) || morphIsPreposition(entry)) return false;
  const label = morphStemLabel(entry);
  return /(^|[\s—(])اسم/.test(label) || label.includes("لفظ الجلالة") || /\bnoun\b|\bproper name\b/i.test(label);
}

// Attached pronoun (ضمير متصل): the trailing pronoun-segment characters of the
// surface token, so only those letters get the pronoun tint. Returns
// { stem, suffix, tail } (tail = trailing punctuation, left untinted) or null
// when the word has no attached pronoun or the segments don't line up with
// the on-screen token (regenerated line text, hamzat-waṣl variants…).
export function morphPronounSuffixSplit(tok, entry) {
  const segs = entry?.segments || [];
  let first = segs.length;
  while (first > 0 && segs[first - 1].type === "pronoun") first -= 1;
  if (first === 0 || first === segs.length) return null;
  const suffix = segs.slice(first).map((s) => s.text || "").join("");
  const tailMatch = tok.match(/[.,!?؟،؛:"«»()[\]]+$/);
  const tail = tailMatch ? tailMatch[0] : "";
  const core = tok.slice(0, tok.length - tail.length);
  if (!suffix || suffix.length >= core.length || !core.endsWith(suffix)) return null;
  return { stem: core.slice(0, core.length - suffix.length), suffix, tail };
}

// Word-type kind for a token given its morphology entry and the pill toggles.
export function morphTokenKind(entry, colorToggles) {
  if (!entry) return "";
  if (colorToggles.verb && morphIsVerb(entry)) return "verb";
  if (colorToggles.pronoun && morphIsPronounWord(entry)) return "pronoun";
  if (colorToggles.prepositions && morphIsPreposition(entry)) return "prep";
  if (colorToggles.noun && morphIsNoun(entry)) return "noun";
  return "";
}

// tokc-* (text-color) class for the same decision — used by flowed text.
// The PDF reader uses the kind directly for tokb-* (box-tint) classes.
export function morphTokenClass(entry, colorToggles) {
  const kind = morphTokenKind(entry, colorToggles);
  return kind ? ` tokc-${kind}` : "";
}
